export const SECRET_DATA_FORMAT_TEXT = 'text'
export const SECRET_DATA_FORMAT_BINARY = 'binary'

export type SecretDataFormat = typeof SECRET_DATA_FORMAT_TEXT | typeof SECRET_DATA_FORMAT_BINARY

export interface SecretDataEntry {
  key: string
  value: string
  format?: SecretDataFormat
}

export interface SplitSecretDataEntriesResult {
  data: Record<string, string>
  binaryData: Record<string, string>
}

export const SECRET_DATA_KEY_MAX_LENGTH = 63
export const SECRET_DATA_KEY_PATTERN = /^[\w.-]+$/

const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/

export const normalizeSecretBinaryValue = (value: string) => value.replace(/\s+/g, '')

export const getSecretDataFormat = (format?: SecretDataFormat): SecretDataFormat => {
  return format === SECRET_DATA_FORMAT_BINARY ? SECRET_DATA_FORMAT_BINARY : SECRET_DATA_FORMAT_TEXT
}

export const isSecretBinaryDataEntry = (entry: Pick<SecretDataEntry, 'format'>) => {
  return getSecretDataFormat(entry.format) === SECRET_DATA_FORMAT_BINARY
}

export const isValidSecretDataKey = (key?: string) => {
  return !!key && key.length <= SECRET_DATA_KEY_MAX_LENGTH && SECRET_DATA_KEY_PATTERN.test(key)
}

export const isValidSecretBinaryValue = (value?: string) => {
  if (!value) {
    return false
  }

  const normalizedValue = normalizeSecretBinaryValue(value)
  return normalizedValue.length > 0 && BASE64_PATTERN.test(normalizedValue)
}

export const readSecretBinaryFileAsBase64 = (file: File) => {
  // eslint-disable-next-line promise/avoid-new
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read binary secret file'))
        return
      }

      resolve(reader.result.split(',', 2)[1] || '')
    })
    reader.addEventListener('error', () => {
      reject(reader.error || new Error('Failed to read binary secret file'))
    })
    reader.readAsDataURL(file)
  })
}

export const hasDuplicateSecretDataKeys = (entries: Pick<SecretDataEntry, 'key'>[]) => {
  const keys = entries.map(entry => entry.key).filter(Boolean)
  return new Set(keys).size !== keys.length
}

export const splitSecretDataEntries = (
  entries: SecretDataEntry[] = []
): SplitSecretDataEntriesResult => {
  return entries.reduce<SplitSecretDataEntriesResult>(
    (result, entry) => {
      if (isSecretBinaryDataEntry(entry)) {
        result.binaryData[entry.key] = normalizeSecretBinaryValue(entry.value)
      } else {
        result.data[entry.key] = entry.value
      }
      return result
    },
    { data: {}, binaryData: {} }
  )
}

export const getSecretDataKeys = (
  data?: Record<string, unknown>,
  binaryData?: Record<string, unknown>
) => {
  return Array.from(new Set([...Object.keys(data || {}), ...Object.keys(binaryData || {})]))
}
